import { Box, Group, Stack, Text } from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import MetadataJsonViewer from '@/components/MetadataJsonViewer.tsx';
import useRetrieveStepExecutionMetadata from '@/hooks/api/stepExecutions/useRetrieveStepExecutionMetadata.ts';
import useRetrieveStepRunLlmUsage from '@/hooks/api/llmUsage/useRetrieveStepRunLlmUsage.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { Run } from '@/types/runs.ts';
import { StepExecution, StepRun } from '@/types/stepExecutions.ts';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import { getStepExecutionTiming } from '@/utils/stepExecutionTiming.ts';

type Props = {
  run: Run;
  step: AutomationStep;
  stepRun?: StepRun;
  stepExecution?: StepExecution;
  enabled: boolean;
  height?: string;
};

const StepExecutionMetadataPanel = ({
  run,
  step,
  stepRun,
  stepExecution,
  enabled,
  height = 'calc(90vh - 170px)',
}: Props) => {
  const stepExecutionIdToFetchMetadata = enabled ? stepExecution?.id : undefined;
  const {
    data: metadata,
    isFetching: loadingMetadata,
  } = useRetrieveStepExecutionMetadata(stepExecutionIdToFetchMetadata);
  const {
    data: llmUsage,
    isFetching: loadingLlmUsage,
  } = useRetrieveStepRunLlmUsage(stepRun?.id, enabled);
  const loadingStepExecutionMetadata = !!stepExecutionIdToFetchMetadata && loadingMetadata;
  const timing = getStepExecutionTiming(stepExecution);
  const codeProvenance = stepExecution?.code_execution_provenance;
  const codeSource = codeProvenance?.used_code_cache
    ? 'Cached generated code'
    : codeProvenance?.effective_code_source === 'provided'
      ? 'Provided code'
      : codeProvenance?.effective_code_source === 'generated'
        ? 'Generated code'
        : null;
  const { code_execution_provenance: _codeExecutionProvenance, ...safeStepExecution } = stepExecution ?? {};
  const { step_execution: _nestedStepExecution, ...safeStepRun } = stepRun ?? {};

  if (!enabled) {
    return null;
  }

  if (!stepExecution) {
    return (
      <Text size="sm" c="dimmed">
        Metadata is available after the step execution is loaded.
      </Text>
    );
  }

  if (loadingStepExecutionMetadata || loadingLlmUsage) {
    return <CustomSpinner size={'md'} />;
  }

  return (
    <>
      <Box
        px="md"
        py="xs"
        bg="#F8F9FA"
        style={{ border: '1px solid #ECECED', borderRadius: '6px' }}
      >
        <Group justify="space-between" align="flex-start" gap="md" wrap="wrap">
          <Stack gap={2}>
            <Text size="xs" c="dimmed" fw={600}>
              Runtime
            </Text>
            <Text size="sm" fw={600}>
              {timing.durationDisplay}
              {timing.state === 'running' ? ' elapsed' : ''}
            </Text>
          </Stack>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed" fw={600}>
              Started
            </Text>
            <Text size="sm" truncate="end">
              {formatCalendarStyle(stepExecution?.created_at)}
            </Text>
          </Stack>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed" fw={600}>
              Finished
            </Text>
            <Text size="sm" truncate="end">
              {timing.state === 'running' ? 'Running' : formatCalendarStyle(stepExecution?.completed_at)}
            </Text>
          </Stack>
          {stepRun?.used_existing_step_execution && (
            <Stack gap={2} style={{ flexShrink: 0 }}>
              <Text size="xs" c="dimmed" fw={600}>
                Source
              </Text>
              <Text size="sm">
                {stepRun.is_preserved_rerun_carrier ? 'Preserved rerun work' : 'Cached'}
              </Text>
            </Stack>
          )}
          {codeSource && (
            <Stack gap={2} style={{ flexShrink: 0 }}>
              <Text size="xs" c="dimmed" fw={600}>
                Code source
              </Text>
              <Text size="sm">{codeSource}</Text>
            </Stack>
          )}
        </Group>
      </Box>
      <Box mt="lg">
        <MetadataJsonViewer
          height={height}
          src={{
            step_run: safeStepRun,
            step_execution: safeStepExecution,
            run: { ...run },
            llm_usage: llmUsage || null,
            logs: metadata || [],
            step: { ...step },
          }}
        />
      </Box>
    </>
  );
};

export default StepExecutionMetadataPanel;
